
import { Component, Vue, Prop } from 'vue-property-decorator';
import shopifyFlowService, { FlowConfig } from '@/services/shopify-flow.service';
import { StoreModel } from '@/models/store';

interface TriggerDisplay {
  key: string;
  title: string;
  description: string;
  icon: string;
  comingSoon: boolean;
}

interface Snackbar {
  show: boolean;
  message: string;
  color: string;
}

@Component({
  name: 'ShopifyFlowSettings',
})
export default class ShopifyFlowSettings extends Vue {
  @Prop({ default: () => new StoreModel() })
  public readonly store!: StoreModel;

  private loading = true;
  private saving = false;

  // Pre-initialize all trigger keys for Vue 2 reactivity
  private config: FlowConfig = {
    enabled: false,
    triggersEnabled: {
      purchase_order_created: false,
      purchase_order_confirmed: false,
      purchase_order_canceled: false,
      purchase_order_completed: false,
      purchase_order_received: false,
      purchase_order_return: false,
      inventory_low: false,
    },
  };

  private snackbar: Snackbar = {
    show: false,
    message: '',
    color: 'success',
  };

  // Computed property for Shopify Flow URL with store name
  private get shopifyFlowUrl(): string {
    const storeName = (window as any).SHOP_NAME || localStorage.getItem('shopify_store') || '';
    // Clean store name: trim, remove .myshopify.com suffix, strip slashes
    const cleanStoreName = storeName
      .trim()
      .replace(/\.myshopify\.com$/i, '')
      .replace(/^\/+|\/+$/g, '');

    // Fallback URL if store name is empty
    if (!cleanStoreName) {
      return 'https://admin.shopify.com/apps/flow';
    }

    return `https://admin.shopify.com/store/${encodeURIComponent(cleanStoreName)}/apps/flow`;
  }

  // Computed property for Calendly URL with shop name and meeting purpose prefilled
  private get calendlyUrl(): string {
    const baseUrl = 'https://calendly.com/assisty/30min';
    const shopName = this.store?.shopName;
    const meetingPurpose = 'Walk through the Shopify Flow integration feature and how to set up a workflow in Shopify';

    const params = new URLSearchParams();
    if (shopName) {
      params.append('a1', shopName);
    }
    params.append('a2', meetingPurpose);

    return `${baseUrl}?${params.toString()}`;
  }

  // Static trigger definitions with display info
  private triggers: TriggerDisplay[] = [
    {
      key: 'purchase_order_created',
      title: 'Purchase Order Created',
      description: 'Triggers when a new purchase order is created (manual or automated)',
      icon: 'mdi-file-document-plus',
      comingSoon: false,
    },
    {
      key: 'purchase_order_confirmed',
      title: 'Purchase Order Confirmed',
      description: 'Triggers when a purchase order is confirmed',
      icon: 'mdi-file-document-check',
      comingSoon: false,
    },
    {
      key: 'purchase_order_canceled',
      title: 'Purchase Order Canceled',
      description: 'Triggers when a purchase order is canceled',
      icon: 'mdi-file-document-remove',
      comingSoon: false,
    },
    {
      key: 'purchase_order_completed',
      title: 'Purchase Order Completed',
      description: 'Triggers when a purchase order is fully completed',
      icon: 'mdi-file-document-check-outline',
      comingSoon: false,
    },
    {
      key: 'purchase_order_received',
      title: 'Purchase Order Received',
      description: 'Triggers when items are received for a purchase order',
      icon: 'mdi-package-variant-closed-check',
      comingSoon: false,
    },
    {
      key: 'purchase_order_return',
      title: 'Purchase Order Return',
      description: 'Triggers when items are returned for a purchase order',
      icon: 'mdi-package-variant-remove',
      comingSoon: false,
    },
    {
      key: 'inventory_low',
      title: 'Inventory Low',
      description: 'Triggers when inventory falls below the reorder point',
      icon: 'mdi-alert-decagram',
      comingSoon: true,
    },
  ];

  async mounted(): Promise<void> {
    await this.loadConfig();
  }

  private async loadConfig(): Promise<void> {
    try {
      this.loading = true;
      const response = await shopifyFlowService.getConfig();

      if (response.data) {
        // Merge server data into existing object to preserve Vue 2 reactivity
        // Coerce to boolean to ensure v-models receive true/false values
        this.config.enabled = Boolean(response.data.enabled);
        // Merge triggersEnabled while keeping pre-initialized keys reactive
        if (response.data.triggersEnabled) {
          Object.keys(this.config.triggersEnabled).forEach((key) => {
            if (key in response.data.triggersEnabled) {
              this.config.triggersEnabled[key] = Boolean(response.data.triggersEnabled[key]);
            }
          });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to load Flow config:', errorMessage);
      this.showSnackbar('Failed to load configuration', 'error');
    } finally {
      this.loading = false;
    }
  }

  private async saveConfig(): Promise<void> {
    try {
      this.saving = true;
      // Only send active triggers (not comingSoon) to avoid backend validation errors
      const filteredTriggersEnabled = this.triggers
        .filter((t) => !t.comingSoon)
        .reduce(
          (acc, t) => {
            acc[t.key] = this.config.triggersEnabled[t.key] || false;
            return acc;
          },
          {} as Record<string, boolean>
        );

      await shopifyFlowService.updateConfig({
        enabled: this.config.enabled,
        triggersEnabled: filteredTriggersEnabled,
      });
      this.showSnackbar('Settings saved successfully', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to save Flow config:', errorMessage);
      this.showSnackbar('Failed to save settings', 'error');
      // Reload to get the correct state
      await this.loadConfig();
    } finally {
      this.saving = false;
    }
  }

  private showSnackbar(message: string, color: string): void {
    this.snackbar = {
      show: true,
      message,
      color,
    };
  }
}
